import { useCallback, useEffect, useRef, useState } from 'react';
import { ServiceConnection } from '../../../redux/api/fymAPI.types';
import { useServiceConnections } from './useServiceConnections';
import { useArtistPortalConnect } from './useArtistPortalConnect';
import { useConnectionInsights } from './useConnectionInsights';
import { useCatalogPortalReturn } from './useCatalogPortalReturn';
import { isSessionCaptureCard } from './sessionCapture/sessionCaptureCardState';
import { useCatalogConnectLink } from './useCatalogConnectLink';
import { isSoundCloudCard } from './soundCloud/soundCloudCardState';
import { useSoundCloudConnect } from '../../music24/soundcloud/useSoundCloudConnect';

/**
 * The service connections of the Catalog screen: the panel data, the artist-portal actions and
 * the state of the connect, profile url, phone handoff and ownership matrix modals.
 */
export function useCatalogConnections({
  accountId,
  isCatalogLoading,
  connectService,
}: {
  accountId: number;
  isCatalogLoading: boolean;
  /** The service of a `/catalog/connect/:service` link: its connect opens once the cards load. */
  connectService?: string;
}) {
  const connections = useServiceConnections({ accountId });
  const portal = useArtistPortalConnect({ accountId });
  // SoundCloud signs in through MusicAPI; its return page goes on to Channels (design soundcloud-connector a, b).
  const soundCloud = useSoundCloudConnect({ accountId });
  const insights = useConnectionInsights({ accountId });

  const [isMatrixOpen, setMatrixOpen] = useState(false);
  const [profileUrlFor, setProfileUrlFor] = useState<ServiceConnection>();
  const [connectFor, setConnectFor] = useState<ServiceConnection>();
  // design: your-intelligence k2 — the phone handoff of a source whose login runs in the app.
  const [handoffFor, setHandoffFor] = useState<ServiceConnection>();
  const closeHandoff = useCallback(() => setHandoffFor(undefined), []);
  // "Choose artist" and "Change artist" of a login that runs in the app (Ditto Music). The web can
  // pick the artist: the login is stored on the server, and the list comes from it.
  const [artistPickFor, setArtistPickFor] = useState<ServiceConnection>();
  const closeArtistPick = useCallback(() => setArtistPickFor(undefined), []);

  const openMatrix = useCallback(() => setMatrixOpen(true), []);
  const closeMatrix = useCallback(() => setMatrixOpen(false), []);
  const closeProfileUrl = useCallback(() => setProfileUrlFor(undefined), []);
  // The connections sit below the catalog, so a sign-in that comes back
  // connected scrolls them into view: the artist sees the card it changed.
  // The scroll waits for the catalog, because a table that loads later would
  // push the cards down again.
  const connectionsRef = useRef<HTMLDivElement>(null);
  const [isConnectionsScrollPending, setConnectionsScrollPending] = useState(false);
  const showConnections = useCallback(() => setConnectionsScrollPending(true), []);
  useEffect(() => {
    if (!isConnectionsScrollPending || isCatalogLoading) {
      return;
    }
    setConnectionsScrollPending(false);
    connectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isCatalogLoading, isConnectionsScrollPending]);
  const portalReturn = useCatalogPortalReturn({
    connections: connections.connections,
    reload: connections.retry,
    openConnect: setConnectFor,
    onConnected: showConnections,
  });
  const { clearCallbackFailure } = portalReturn;
  const { resetWriteError } = connections;
  const closeConnect = useCallback(() => {
    setConnectFor(undefined);
    clearCallbackFailure();
    resetWriteError();
  }, [clearCallbackFailure, resetWriteError]);

  const findConnection = useCallback(
    (integrationType: string) => connections.connections?.find((c) => c.integrationType === integrationType),
    [connections.connections]
  );

  const onEditProfileUrl = useCallback(
    (integrationType: string) => {
      setConnectFor(undefined);
      clearCallbackFailure();
      resetWriteError();
      setProfileUrlFor(findConnection(integrationType));
    },
    [clearCallbackFailure, findConnection, resetWriteError]
  );

  const onConnect = useCallback(
    (integrationType: string) => {
      clearCallbackFailure();
      resetWriteError();
      const connection = findConnection(integrationType);
      if (connection !== undefined && isSessionCaptureCard(connection)) {
        setConnectFor(undefined);
        setHandoffFor(connection);
        return;
      }
      // A service with no sign-in yet (Amazon Music for Artists) has nothing to connect: its card
      // says "Coming soon" and offers only the profile url.
      if (connection?.earlyAccess === true) {
        setConnectFor(undefined);
        return;
      }
      if (connection !== undefined && isSoundCloudCard(connection)) {
        setConnectFor(undefined);
        soundCloud.connect().catch(console.error);
        return;
      }
      setConnectFor(connection);
    },
    [clearCallbackFailure, findConnection, resetWriteError, soundCloud]
  );

  useCatalogConnectLink({ service: connectService, connections: connections.connections, onConnect });

  const onSyncConnection = useCallback(
    (integrationType: string) => {
      portal.sync(integrationType).catch(console.error);
    },
    [portal]
  );

  const onDisconnectConnection = useCallback(
    (integrationType: string) => {
      portal.disconnect(integrationType).catch(console.error);
    },
    [portal]
  );

  const onChooseArtist = useCallback(
    (integrationType: string) => {
      portal.clearError();
      setArtistPickFor(findConnection(integrationType));
    },
    [findConnection, portal]
  );
  /** The pick is saved and the read starts on the server: close the picker and show the card. */
  const onArtistPicked = useCallback(() => {
    setArtistPickFor(undefined);
    showConnections();
  }, [showConnections]);

  return {
    connections,
    connectionsRef,
    portal,
    insights,
    isMatrixOpen,
    openMatrix,
    closeMatrix,
    profileUrlFor,
    closeProfileUrl,
    connectFor,
    closeConnect,
    handoffFor,
    closeHandoff,
    artistPickFor,
    closeArtistPick,
    onChooseArtist,
    onArtistPicked,
    callbackFailure: portalReturn.callbackFailure,
    onEditProfileUrl,
    onConnect,
    onSyncConnection,
    onDisconnectConnection,
  };
}

export type CatalogConnections = ReturnType<typeof useCatalogConnections>;
